import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection, getDocs, addDoc, updateDoc,
  doc, Timestamp, query, orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const emptyForm = {
  title: '',
  date: '',
  time: '',
  is_multi_day: false,
  end_date: '',
  end_time: '',
  location: '',
  details: '',
  cost: '',
  signup_deadline: '',
  status: 'published',
  scheduled_at: ''
};

const StatusBadge = ({ status }) => {
  const map = {
    published: { bg: '#2a6041', label: 'Published' },
    draft: { bg: '#666', label: 'Draft' },
    scheduled: { bg: '#1a4f8a', label: 'Scheduled' },
    deleted: { bg: '#ccc', color: '#666', label: 'Deleted' }
  };
  const s = map[status] || map.published;
  return (
    <span style={{
      background: s.bg, color: s.color || 'white',
      fontSize: '0.68rem', padding: '2px 8px', borderRadius: '10px',
      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {s.label}
    </span>
  );
};

const AdminEvents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
      } else if (config.configLoaded) {
        fetchEvents().then(() => setLoading(false));
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    const snap = await getDocs(query(collection(db, 'events'), orderBy('date', 'asc')));
    setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const parseToTimestamp = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const combined = timeStr ? `${dateStr}T${timeStr}` : `${dateStr}T00:00`;
    return Timestamp.fromDate(new Date(combined));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tsToDateStr = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const tsToTimeStr = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const tsToDatetimeLocal = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const effectiveStatus = (item) => {
    if (item.status === 'scheduled' && item.scheduled_at) {
      const sat = item.scheduled_at.toDate ? item.scheduled_at.toDate() : new Date(item.scheduled_at);
      if (sat <= new Date()) return 'published';
    }
    return item.status || 'published';
  };

  const handleEdit = (event) => {
    setForm({
      title: event.title || '',
      date: tsToDateStr(event.date),
      time: tsToTimeStr(event.date),
      is_multi_day: !!event.end_date,
      end_date: tsToDateStr(event.end_date),
      end_time: tsToTimeStr(event.end_date),
      location: event.location || '',
      details: event.details || '',
      cost: event.cost || event.fee || '',
      signup_deadline: tsToDateStr(event.signup_deadline),
      status: event.status || 'published',
      scheduled_at: tsToDatetimeLocal(event.scheduled_at)
    });
    setEditingId(event.id);
    setSaveMsg('');
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event? It will be hidden from the website.')) return;
    await updateDoc(doc(db, 'events', id), { status: 'deleted' });
    await fetchEvents();
  };

  const handleRestore = async (id) => {
    await updateDoc(doc(db, 'events', id), { status: 'published' });
    await fetchEvents();
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) {
      setSaveMsg('Title and date are required.');
      return;
    }
    if (form.status === 'scheduled' && !form.scheduled_at) {
      setSaveMsg('Publish date/time is required for scheduled status.');
      return;
    }
    setSaving(true);
    setSaveMsg('');
    try {
      const data = {
        title: form.title.trim(),
        date: parseToTimestamp(form.date, form.time),
        end_date: form.is_multi_day && form.end_date ? parseToTimestamp(form.end_date, form.end_time) : null,
        location: form.location.trim(),
        details: form.details.trim(),
        cost: form.cost.trim(),
        signup_deadline: form.signup_deadline ? parseToTimestamp(form.signup_deadline, '') : null,
        status: form.status,
        scheduled_at: form.status === 'scheduled' && form.scheduled_at
          ? Timestamp.fromDate(new Date(form.scheduled_at))
          : null
      };
      if (editingId) {
        await updateDoc(doc(db, 'events', editingId), data);
      } else {
        data.created_at = Timestamp.fromDate(new Date());
        await addDoc(collection(db, 'events'), data);
      }
      setForm(emptyForm);
      setEditingId(null);
      setSaveMsg('Saved!');
      await fetchEvents();
    } catch (err) {
      console.error(err);
      setSaveMsg('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const now = new Date();
  const visibleEvents = showDeleted ? events : events.filter(e => e.status !== 'deleted');
  const upcomingEvents = visibleEvents.filter(e => !e.date || e.date.toDate() >= now);
  const pastEvents = visibleEvents.filter(e => e.date && e.date.toDate() < now);

  if (loading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin" className="stt-admin-back">← Back to Admin</a>
      <h1 className="stt-admin-title">Events</h1>
      <hr />

      {/* Events table */}
      <div className="stt-admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>
            {upcomingEvents.length} upcoming event{upcomingEvents.length !== 1 ? 's' : ''}
          </span>
          <label style={{ fontSize: '0.82rem', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
            <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
            Show deleted
          </label>
        </div>
        {upcomingEvents.length === 0 ? (
          <p style={{ color: '#888' }}>No upcoming events.</p>
        ) : (
          <div className="stt-admin-list">
            {upcomingEvents.map(event => (
              <div key={event.id} className={`stt-admin-list-row${event.status === 'deleted' ? ' deleted' : ''}`}>
                <div className="stt-admin-list-info">
                  <div className="stt-admin-list-title">{event.title}</div>
                  <div className="stt-admin-list-meta">
                    {formatDate(event.date)}
                    <StatusBadge status={effectiveStatus(event)} />
                  </div>
                </div>
                <div className="stt-admin-list-actions">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`/events/${event.id}`)}>View</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(event)}>Edit</button>
                  {event.status === 'deleted' ? (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRestore(event.id)}>Restore</button>
                  ) : (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(event.id)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Past events collapsible */}
        {pastEvents.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={() => setShowPast(p => !p)}
              style={{
                background: 'none', border: 'none', padding: 0,
                fontSize: '0.82rem', color: '#888', cursor: 'pointer',
                textDecoration: 'underline', textUnderlineOffset: '3px'
              }}
            >
              {showPast ? 'Hide' : 'Show'} past events ({pastEvents.length})
            </button>
            {showPast && (
              <div className="stt-admin-list" style={{ marginTop: '12px' }}>
                {pastEvents.map(event => (
                  <div key={event.id} className={`stt-admin-list-row${event.status === 'deleted' ? ' deleted' : ''}`}>
                    <div className="stt-admin-list-info">
                      <div className="stt-admin-list-title">{event.title}</div>
                      <div className="stt-admin-list-meta">
                        {formatDate(event.date)}
                        <StatusBadge status={effectiveStatus(event)} />
                      </div>
                    </div>
                    <div className="stt-admin-list-actions">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`/events/${event.id}`)}>View</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(event)}>Edit</button>
                      {event.status === 'deleted' ? (
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRestore(event.id)}>Restore</button>
                      ) : (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(event.id)}>Delete</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.2rem', marginBottom: '20px', color: 'var(--navy)' }}>
          {editingId ? 'Edit Event' : 'Add New Event'}
        </h2>

        <div className="mb-3">
          <label className="stt-form-label">Event Title *</label>
          <input type="text" className="form-control" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        </div>

        <div className="row mb-3">
          <div className="col-12 col-md-6 mb-3 mb-md-0">
            <label className="stt-form-label">Start Date *</label>
            <input type="date" className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="col-12 col-md-6">
            <label className="stt-form-label">Start Time (optional)</label>
            <input type="time" className="form-control" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} />
          </div>
        </div>

        <div className="mb-3">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={form.is_multi_day}
              onChange={e => setForm({ ...form, is_multi_day: e.target.checked })}
            />
            <span className="stt-form-label" style={{ marginBottom: 0 }}>Multi-day event</span>
          </label>
        </div>

        {form.is_multi_day && (
          <div className="row mb-3">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <label className="stt-form-label">End Date</label>
              <input type="date" className="form-control" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="col-12 col-md-6">
              <label className="stt-form-label">End Time (optional)</label>
              <input type="time" className="form-control" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} />
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="stt-form-label">Location</label>
          <input type="text" className="form-control" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Gate of Heaven" />
        </div>

        <div className="mb-3">
          <label className="stt-form-label">Details</label>
          <textarea className="form-control" rows={3} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} placeholder="Description, additional info, etc." />
        </div>

        <div className="row mb-3">
          <div className="col-12 col-md-6 mb-3 mb-md-0">
            <label className="stt-form-label">Cost (optional)</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input type="text" className="form-control" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} placeholder="25" />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <label className="stt-form-label">Sign-up Deadline (optional)</label>
            <input type="date" className="form-control" value={form.signup_deadline} onChange={e => setForm({ ...form, signup_deadline: e.target.value })} />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-12 col-md-6 mb-3 mb-md-0">
            <label className="stt-form-label">Status</label>
            <select className="form-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>
          {form.status === 'scheduled' && (
            <div className="col-12 col-md-6">
              <label className="stt-form-label">Publish Date &amp; Time *</label>
              <input
                type="datetime-local"
                className="form-control"
                value={form.scheduled_at}
                onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
              />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button className="btn stt-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update Event' : 'Add Event'}
          </button>
          {editingId && (
            <button className="btn btn-outline-secondary" onClick={() => { setForm(emptyForm); setEditingId(null); setSaveMsg(''); }}>
              Cancel
            </button>
          )}
          {saveMsg && (
            <span className={`stt-save-message ${saveMsg === 'Saved!' ? 'success' : 'error'}`}>
              {saveMsg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEvents;
